import { useCallback, useEffect, useRef, useState } from 'react';
import businessBuildIllustration from '@Assets/dnbEmbeddedIntro/businessbuild-illustration.png';
import savingsIllustration from '@Assets/dnbEmbeddedIntro/savings-illustration.png';
import paymentsIllustration from '@Assets/dnbEmbeddedIntro/payments-illustration.png';
import loansIllustration from '@Assets/dnbEmbeddedIntro/loans-illustration.png';
// The tablet and mobile carousels share a compact (landscape) illustration composition — see
// the Figma `DNB intro-Image-mobile` component set. The tablet art is exported at 2x and the
// mobile reuses it (downscaled), so there is a single asset per slide.
import businessBuildIllustrationCompact from '@Assets/dnbEmbeddedIntro/businessbuild-illustration-compact.png';
import savingsIllustrationCompact from '@Assets/dnbEmbeddedIntro/savings-illustration-compact.png';
import paymentsIllustrationCompact from '@Assets/dnbEmbeddedIntro/payments-illustration-compact.png';
import loansIllustrationCompact from '@Assets/dnbEmbeddedIntro/loans-illustration-compact.png';

const SLIDE_DURATION_MS = 4000;

export type SlideKind = 'businessBuild' | 'savings' | 'payments' | 'loans';

export interface Slide {
  kind: SlideKind;
  // Desktop composition (wide layout)
  image: string;
  width: number;
  height: number;
  // Compact composition shared by the tablet and mobile carousels (exported at 2x for the tablet size)
  compactImage: string;
  tabletWidth: number;
  tabletHeight: number;
  mobileWidth: number;
  mobileHeight: number;
}

// Illustrations are exported from Figma at 2x scale; width/height are the intended 1x display size.
export const SLIDES: Slide[] = [
  {
    kind: 'businessBuild',
    image: businessBuildIllustration,
    width: 579,
    height: 338,
    compactImage: businessBuildIllustrationCompact,
    tabletWidth: 328,
    tabletHeight: 233,
    mobileWidth: 259,
    mobileHeight: 184,
  },
  {
    kind: 'savings',
    image: savingsIllustration,
    width: 273,
    height: 386,
    compactImage: savingsIllustrationCompact,
    tabletWidth: 236,
    tabletHeight: 185,
    mobileWidth: 215,
    mobileHeight: 168,
  },
  {
    kind: 'payments',
    image: paymentsIllustration,
    width: 492,
    height: 288,
    compactImage: paymentsIllustrationCompact,
    tabletWidth: 202,
    tabletHeight: 185,
    mobileWidth: 189,
    mobileHeight: 173,
  },
  {
    kind: 'loans',
    image: loansIllustration,
    width: 290,
    height: 374,
    compactImage: loansIllustrationCompact,
    tabletWidth: 195,
    tabletHeight: 209,
    mobileWidth: 195,
    mobileHeight: 209,
  },
];

export interface UseDnbCarouselLogicOptions {
  // Progress changes every animation frame, so only opt in where it is actually rendered (desktop).
  withProgress?: boolean;
}

export const useDnbCarouselLogic = ({ withProgress = false }: UseDnbCarouselLogicOptions = {}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const rafIdRef = useRef<number | undefined>(undefined);
  const segmentStartRef = useRef(performance.now());
  const progressRef = useRef(0);

  const goToSlide = useCallback((index: number) => {
    const nextIndex = (index + SLIDES.length) % SLIDES.length;
    setActiveIndex(nextIndex);
    progressRef.current = 0;
    setProgress(0);
    segmentStartRef.current = performance.now();
  }, []);

  const handleNext = useCallback(() => goToSlide(activeIndex + 1), [activeIndex, goToSlide]);
  const handlePrev = useCallback(() => {
    if (activeIndex === 0) return;
    goToSlide(activeIndex - 1);
  }, [activeIndex, goToSlide]);

  useEffect(() => {
    if (!isPlaying) return undefined;

    segmentStartRef.current = performance.now() - progressRef.current * SLIDE_DURATION_MS;

    const step = (now: number) => {
      const elapsed = (now - segmentStartRef.current) / SLIDE_DURATION_MS;
      if (elapsed >= 1) {
        progressRef.current = 0;
        if (withProgress) setProgress(0);
        setActiveIndex((prev) => (prev + 1) % SLIDES.length);
        segmentStartRef.current = now;
        rafIdRef.current = requestAnimationFrame(step);
        return;
      }
      progressRef.current = elapsed;
      if (withProgress) setProgress(elapsed);
      rafIdRef.current = requestAnimationFrame(step);
    };

    rafIdRef.current = requestAnimationFrame(step);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [isPlaying, withProgress]);

  const togglePlaying = () => setIsPlaying((prev) => !prev);

  return {
    slides: SLIDES,
    activeIndex,
    isPlaying,
    progress,
    goToSlide,
    handleNext,
    handlePrev,
    togglePlaying,
  };
};
